/**
 * Quiz Config Service
 * 
 * Handles loading/caching of dynamic quiz step configurations.
 * Offline-first: loads from localStorage, then syncs from Edge Function.
 * 
 * Features:
 * - Local caching with TTL
 * - Version-based cache invalidation
 * - Fallback to hardcoded defaults if offline and no cache
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';

// ============= TYPES =============

export interface QuizStepOption {
  value: string;
  label: string;
}

export interface QuizStepConditionalBlock {
  showBlock: string;
  type: 'multi' | 'dropdown';
  options?: QuizStepOption[];
  sourceTable?: string;
  validation?: {
    required?: boolean;
    minSelected?: number;
  };
}

export interface QuizStepAssets {
  radioDefault?: string;
  radioSelected?: string;
  checkboxDefault?: string;
  checkboxSelected?: string;
  dropdownArrow?: string;
}

export interface QuizStepConfig {
  id: string;
  stepNumber: number;
  screenId: string;
  questionType: 'single' | 'multi' | 'dropdown' | 'slider' | 'conditional';
  questionText: string;
  options: QuizStepOption[];
  validationRules: {
    required?: boolean;
    minSelected?: number;
    maxSelected?: number;
  };
  conditionalLogic?: Record<string, QuizStepConditionalBlock>;
  assets: QuizStepAssets;
  isActive: boolean;
  sortOrder: number;
}

export interface QuizConfig {
  steps: QuizStepConfig[];
  version: string;
  cachedAt: number;
  ttlHours: number;
}

// ============= CONSTANTS =============

const STORAGE_KEY = 'quiz_config_cache';
const DEFAULT_TTL_HOURS = 24;
const DEFAULT_VERSION = '1';

// Fallback steps for offline mode
const FALLBACK_STEPS: QuizStepConfig[] = [
  {
    id: 'fallback-1',
    stepNumber: 1,
    screenId: 'QUIZ-002-2',
    questionType: 'conditional',
    questionText: 'Хотите питомца определённой породы?',
    options: [
      { value: 'any', label: 'Люблю всех собак на Земле' },
      { value: 'type', label: 'Точно знаю, какой тип мне подойдёт' },
      { value: 'breed', label: 'Мечтаю о собаке конкретной породы' },
    ],
    validationRules: { required: true },
    conditionalLogic: {
      type: {
        showBlock: 'breedTypes',
        type: 'multi',
        options: [
          { value: 'hunting', label: 'Охотничьи' },
          { value: 'service', label: 'Служебные' },
          { value: 'decorative', label: 'Декоративные' },
        ],
        validation: { minSelected: 1 },
      },
      breed: {
        showBlock: 'breedSelect',
        type: 'dropdown',
        sourceTable: 'breeds',
        validation: { required: true },
      },
    },
    assets: {
      radioDefault: '/assets/quiz-radio-default.png',
      radioSelected: '/assets/quiz-radio-selected.png',
      checkboxDefault: '/assets/quiz-checkbox-default.png',
      checkboxSelected: '/assets/quiz-checkbox-selected.png',
    },
    isActive: true,
    sortOrder: 10,
  },
  {
    id: 'fallback-2',
    stepNumber: 2,
    screenId: 'QUIZ-002-3',
    questionType: 'single',
    questionText: 'А пол питомца вам важен?',
    options: [
      { value: 'male', label: 'Мальчик' },
      { value: 'female', label: 'Девочка' },
      { value: 'any', label: 'Не имеет значения' },
    ],
    validationRules: { required: true },
    assets: {
      radioDefault: '/assets/quiz-radio-default.png',
      radioSelected: '/assets/quiz-radio-selected.png',
    },
    isActive: true,
    sortOrder: 20,
  },
  {
    id: 'fallback-3',
    stepNumber: 3,
    screenId: 'QUIZ-002-4',
    questionType: 'single',
    questionText: 'Какой возраст питомца вам подходит?',
    options: [
      { value: 'puppy', label: 'Щенок (до 1 года)' },
      { value: 'young', label: 'Молодой (1-3 года)' },
      { value: 'adult', label: 'Взрослый (3-7 лет)' },
      { value: 'senior', label: 'Пожилой (7+ лет)' },
      { value: 'any', label: 'Любой возраст' },
    ],
    validationRules: { required: true },
    assets: {},
    isActive: true,
    sortOrder: 30,
  },
];

// ============= CACHE FUNCTIONS =============

function getCachedConfig(): QuizConfig | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    
    const config: QuizConfig = JSON.parse(raw);
    
    // Check if cache is still valid
    const ttlMs = config.ttlHours * 60 * 60 * 1000;
    if (Date.now() - config.cachedAt > ttlMs) {
      logger.debug('Quiz config cache expired');
      return null;
    }
    
    return config;
  } catch (error) {
    logger.warn('Failed to read quiz config cache', { error });
    return null;
  }
}

function setCachedConfig(config: QuizConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    logger.warn('Failed to save quiz config cache', { error });
  }
}

function clearConfigCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.warn('Failed to clear quiz config cache', { error });
  }
}

// ============= DATA TRANSFORMATION =============

function transformDbRow(row: Record<string, unknown>): QuizStepConfig {
  return {
    id: row.id as string,
    stepNumber: row.step_number as number,
    screenId: row.screen_id as string,
    questionType: row.question_type as QuizStepConfig['questionType'],
    questionText: row.question_text as string,
    options: (row.options || []) as QuizStepOption[],
    validationRules: (row.validation_rules || {}) as QuizStepConfig['validationRules'],
    conditionalLogic: row.conditional_logic as Record<string, QuizStepConditionalBlock> | undefined,
    assets: (row.assets || {}) as QuizStepAssets,
    isActive: row.is_active as boolean,
    sortOrder: row.sort_order as number,
  };
}

// ============= MAIN FUNCTIONS =============

/**
 * Load quiz config from local cache or database
 * Falls back to hardcoded defaults if offline and no cache
 */
export async function loadQuizConfig(): Promise<QuizConfig> {
  // Try cache first
  const cached = getCachedConfig();
  if (cached && cached.steps.length > 0) {
    logger.debug('Loaded quiz config from cache', { 
      data: { version: cached.version, stepsCount: cached.steps.length },
      allowList: ['version', 'stepsCount'],
    });
    
    // Background refresh if cache is older than half TTL
    const halfTtlMs = (cached.ttlHours / 2) * 60 * 60 * 1000;
    if (Date.now() - cached.cachedAt > halfTtlMs) {
      refreshConfigInBackground();
    }
    
    return cached;
  }
  
  // Try to fetch from database
  try {
    const { data, error } = await supabase
      .from('quiz_steps')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    
    if (error) throw error;
    
    if (data && data.length > 0) {
      const steps = data.map(transformDbRow);
      
      // Get config version from app_settings
      const { data: settingsData } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'quiz_config_version')
        .single();
      
      const { data: ttlData } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'quiz_config_cache_ttl_hours')
        .single();
      
      const config: QuizConfig = {
        steps,
        version: settingsData?.value || DEFAULT_VERSION,
        cachedAt: Date.now(),
        ttlHours: parseInt(ttlData?.value || String(DEFAULT_TTL_HOURS), 10),
      };
      
      setCachedConfig(config);
      
      logger.debug('Loaded quiz config from database', {
        data: { version: config.version, stepsCount: steps.length },
        allowList: ['version', 'stepsCount'],
      });
      
      return config;
    }
  } catch (error) {
    logger.warn('Failed to load quiz config from database', { error });
  }
  
  // Fallback to hardcoded steps
  logger.warn('Using fallback quiz config');
  return {
    steps: FALLBACK_STEPS,
    version: DEFAULT_VERSION,
    cachedAt: Date.now(),
    ttlHours: DEFAULT_TTL_HOURS,
  };
}

/**
 * Background refresh without blocking
 */
async function refreshConfigInBackground(): Promise<void> {
  try {
    const { data, error } = await supabase
      .from('quiz_steps')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    
    if (error || !data || data.length === 0) return;
    
    const steps = data.map(transformDbRow);
    
    const { data: settingsData } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'quiz_config_version')
      .single();
    
    const { data: ttlData } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'quiz_config_cache_ttl_hours')
      .single();
    
    const config: QuizConfig = {
      steps,
      version: settingsData?.value || DEFAULT_VERSION,
      cachedAt: Date.now(),
      ttlHours: parseInt(ttlData?.value || String(DEFAULT_TTL_HOURS), 10),
    };
    
    setCachedConfig(config);
    logger.debug('Quiz config refreshed in background');
  } catch {
    // Silently fail background refresh
  }
}

/**
 * Get step config by step number
 */
export function getStepByNumber(config: QuizConfig, stepNumber: number): QuizStepConfig | undefined {
  return config.steps.find(s => s.stepNumber === stepNumber);
}

/**
 * Get step config by screen ID
 */
export function getStepByScreenId(config: QuizConfig, screenId: string): QuizStepConfig | undefined {
  return config.steps.find(s => s.screenId === screenId);
}

/**
 * Get total number of active steps
 */
export function getTotalSteps(config: QuizConfig): number {
  return config.steps.length;
}

/**
 * Force refresh config from database
 */
export async function refreshQuizConfig(): Promise<QuizConfig> {
  clearConfigCache();
  return loadQuizConfig();
}

/**
 * Check if config needs refresh based on version
 */
export async function checkConfigVersion(): Promise<boolean> {
  const cached = getCachedConfig();
  if (!cached) return true;
  
  try {
    const { data } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'quiz_config_version')
      .single();
    
    return data?.value !== cached.version;
  } catch {
    return false;
  }
}
